import React from 'react';
import { CellPosition } from '../types';
import { getColumnIndex, numberToColumnLetter } from '../utils';

interface UseSpreadsheetNavigationProps {
  rows: number;
  columns: number;
  calculatedRows: number;
  calculatedColumns: number;
  autoFillSpace: boolean;
  activeCell: CellPosition | null;
  navigateToCell: (position: CellPosition, shiftKey?: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement>;
}

export function useSpreadsheetNavigation({
  rows,
  columns,
  calculatedRows,
  calculatedColumns,
  autoFillSpace,
  activeCell,
  navigateToCell,
  containerRef
}: UseSpreadsheetNavigationProps) {
  // Ensure container has focus for keyboard events
  const ensureContainerFocus = React.useCallback(() => {
    if (containerRef.current && document.activeElement !== containerRef.current) {
      containerRef.current.focus();
    }
  }, [containerRef]);

  // Handle keyboard navigation and shortcuts
  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    // Handle Escape to ensure focus returns to container
    if (event.key === 'Escape') {
      event.preventDefault();
      if (containerRef.current) {
        containerRef.current.focus();
      }
      return;
    }

    if (!activeCell) return;

    const { rowIdx, columnKey } = activeCell;
    const colIdx = getColumnIndex(columnKey);
    
    if (colIdx === -1) return;

    let newRowIdx = rowIdx;
    let newColIdx = colIdx;

    switch (event.key) {
      case 'ArrowUp':
        newRowIdx = Math.max(0, rowIdx - 1);
        break;
      case 'ArrowDown':
        newRowIdx = Math.min((autoFillSpace ? calculatedRows : rows) - 1, rowIdx + 1);
        break;
      case 'ArrowLeft':
        newColIdx = Math.max(0, colIdx - 1);
        break;
      case 'ArrowRight':
        newColIdx = Math.min((autoFillSpace ? calculatedColumns : columns) - 1, colIdx + 1);
        break;
      default:
        return;
    }

    event.preventDefault();
    const newColumnKey = numberToColumnLetter(newColIdx);
    const newPosition = { rowIdx: newRowIdx, columnKey: newColumnKey };
    
    navigateToCell(newPosition, event.shiftKey);
  }, [activeCell, rows, columns, calculatedRows, calculatedColumns, autoFillSpace, navigateToCell, containerRef]);

  // Handle input-specific keyboard events
  const handleInputKeyDown = React.useCallback((
    event: React.KeyboardEvent, 
    rowIdx: number, 
    columnKey: string
  ) => {
    // Handle navigation keys in input fields
    const input = event.target as HTMLInputElement;
    const cursorAtStart = input.selectionStart === 0;
    const cursorAtEnd = input.selectionEnd === input.value.length;
    const noTextSelected = input.selectionStart === input.selectionEnd;

    if (noTextSelected) {
      let shouldPreventDefault = false;
      let newRowIdx = rowIdx;
      let newColIdx = getColumnIndex(columnKey);

      switch (event.key) {
        case 'ArrowUp':
          newRowIdx = Math.max(0, rowIdx - 1);
          shouldPreventDefault = true;
          break;
        case 'ArrowDown':
        case 'Enter':
          newRowIdx = Math.min((autoFillSpace ? calculatedRows : rows) - 1, rowIdx + 1);
          shouldPreventDefault = true;
          break;
        case 'ArrowLeft':
          if (cursorAtStart) {
            newColIdx = Math.max(0, newColIdx - 1);
            shouldPreventDefault = true;
          }
          break;
        case 'ArrowRight':
        case 'Tab':
          if (cursorAtEnd || event.key === 'Tab') {
            newColIdx = Math.min((autoFillSpace ? calculatedColumns : columns) - 1, newColIdx + 1);
            shouldPreventDefault = true;
          }
          break;
        case 'Escape':
          // Return focus to container
          event.preventDefault();
          if (containerRef.current) {
            containerRef.current.focus();
          }
          return;
      }

      if (shouldPreventDefault && (newRowIdx !== rowIdx || newColIdx !== getColumnIndex(columnKey))) {
        event.preventDefault();
        const newColumnKey = numberToColumnLetter(newColIdx);
        const newPosition = { rowIdx: newRowIdx, columnKey: newColumnKey };
        
        navigateToCell(newPosition, event.shiftKey);
        
        // Focus the new cell
        setTimeout(() => {
          const newInput = document.querySelector(
            `input[data-row="${newRowIdx}"][data-col="${newColumnKey}"]`
          ) as HTMLInputElement;
          if (newInput) {
            newInput.focus();
            newInput.select();
          }
        }, 0);
      }
    }
  }, [rows, columns, calculatedRows, calculatedColumns, autoFillSpace, navigateToCell, containerRef]);

  // Initialize focus and ensure proper setup
  React.useEffect(() => {
    // Set initial focus to the container after mount
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [containerRef]);

  return {
    ensureContainerFocus,
    handleKeyDown,
    handleInputKeyDown
  };
} 